import { forwardRef, useState, MouseEvent, ReactNode } from 'react'
import { useTranslation } from 'next-i18next'
import html from 'html-react-parser'

import { css } from '@emotion/react'

import Error from '@/components/form/Error'

import { getNavigationIcon } from '@/constants/navigationAssets'
import { mediaUp } from '@/styles/media'
import { Text2 } from '@/styles/typography'
import { visuallyHidden, focusVisible } from '@/styles/mixins'
import { border, colors, transitions } from '@/styles/theme'

interface Props {
  value: boolean
  error?: string
  label?: string | ReactNode
  labelLong?: string | ReactNode
  disabled?: boolean
  greenStyle?: boolean
  onChange(value: boolean): void
  onBlur?(): void
  dataTestId?: string
}

const Checkbox = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const {
    value,
    label,
    labelLong,
    disabled = false,
    greenStyle = false,
    error,
    onChange,
    onBlur,
    dataTestId
  } = props

  const { t } = useTranslation('common')
  const [expanded, setExpanded] = useState(false)

  const toggle = () => onChange(!value)
  const handleExpand = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setExpanded(!expanded)
  }

  const normalizeLabel = (label: string | ReactNode) => {
    return typeof label === 'string' ? html(label) : label
  }

  return (
    <div>
      <label css={checkboxComponent}>
        <input
          css={input}
          ref={ref}
          type="checkbox"
          checked={value}
          disabled={disabled}
          onChange={toggle}
          onBlur={onBlur}
          data-test-id={dataTestId}
        />
        <span css={[customCheckbox, greenStyle && greenCustomCheckbox]}>
          <img
            src={getNavigationIcon('check')}
            alt="Check mark"
            style={{
              width: '70%',
              height: '70%',
              filter: 'brightness(0) invert(1)'
            }}
          />
        </span>
        {label && (
          <Text2 css={[checkboxLabel, greenStyle && greenCheckboxLabel]}>
            {labelLong &&
              (expanded ? (
                normalizeLabel(labelLong)
              ) : (
                <>
                  {normalizeLabel(label)}
                  <button
                    onClick={handleExpand}
                    type="button"
                    css={[showMoreLabel, greenStyle && greenShowMoreLabel]}
                  >
                    {t('form.more')}
                  </button>
                </>
              ))}
            {!labelLong && normalizeLabel(label)}
          </Text2>
        )}
      </label>
      {error && <Error error={error} />}
    </div>
  )
})

const checkboxComponent = css`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  cursor: pointer;
`

const input = css`
  ${visuallyHidden}
`

const customCheckbox = css`
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  margin-inline-end: 10px;
  background-color: ${colors.white};
  border: solid 1px transparent;
  border-radius: 6px;
  box-shadow: ${border.shadow};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color ${transitions.base};

  input:checked + & {
    background-color: var(--accentFirst);
  }

  input:disabled + & {
    pointer-events: none;
    opacity: 0.3;
  }

  ${mediaUp('lg')} {
    width: 38px;
    height: 38px;
    margin-inline-end: 20px;
    border-radius: 10px;
    border-width: 2px;
  }

  label:hover input:not(:checked) + &,
  input:not(:checked):focus + & {
    border-color: var(--accentFirst);
  }

  svg,
  img {
    width: 70%;
    height: 70%;
    stroke: ${colors.white};

    label:hover input:not(:checked) + label & {
      stroke: var(--accentFirst);
    }
  }
`

const greenCustomCheckbox = css`
  input:checked + & {
    background-color: var(--accentSecond);
  }

  label:hover input:not(:checked) + &,
  input:not(:checked):focus + & {
    border-color: var(--accentSecond);
  }

  svg,
  img {
    label:hover input:not(:checked) + label & {
      stroke: var(--accentSecond);
    }
  }
`

const checkboxLabel = css`
  margin-top: 5px;
  position: relative;
  z-index: 0;

  a {
    ${focusVisible};
    color: var(--accentFirst);
    text-decoration: none;
    z-index: 1;
    position: relative;

    &:hover {
      text-decoration: underline;
    }
  }

  input:disabled ~ & {
    pointer-events: none !important;
    opacity: 0.3 !important;
  }
`

const greenCheckboxLabel = css`
  color: ${colors.white};

  a {
    color: var(--accentSecond);
  }
`

const showMoreLabel = css`
  ${focusVisible};
  color: var(--accentFirst);
  z-index: 1;
  position: relative;

  &:hover {
    text-decoration: underline;
  }
`

const greenShowMoreLabel = css`
  color: var(--accentSecond);
`

export default Checkbox
